export const formatDateTime = (value) => {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const toTitleCase = (value = '') => (
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase())
);

export const formatMessageKind = (message = {}) => {
  const type = message.message_type || 'message';
  const subtype = message.message_subtype ? ` / ${toTitleCase(message.message_subtype)}` : '';
  return `${toTitleCase(type)}${subtype}`;
};

export const getMessageTimestamp = (message = {}) => (
  message.header?.timestamp || message.created_at || ''
);

export const isMessageKind = (message = {}, type, subtype) => (
  String(message.message_type || '').toLowerCase() === type &&
  String(message.message_subtype || '').toLowerCase() === subtype
);

export const getMessageSchemaRef = (message = {}) => {
  const schemaId = message.shared_schema_id || message.header?.schemaId || '';
  const version = message.header?.schemaVersion || message.schema_version || message.shared_schema_version || '';

  if (!schemaId || !version) {
    return null;
  }

  return {
    schemaId,
    version,
    key: `${schemaId}@${version}`,
  };
};

export const getMessageSchema = (message, schemaCache = {}) => {
  const ref = getMessageSchemaRef(message);
  return ref ? schemaCache[ref.key] : null;
};

export const getSchemaFields = (schema) => {
  if (Array.isArray(schema?.body)) {
    return schema.body;
  }

  if (Array.isArray(schema?.fields)) {
    return schema.fields;
  }

  return [];
};

export const getFieldLabel = (field, fallbackName) => (
  field?.label || toTitleCase(field?.name || fallbackName || '')
);

export const hasRenderableValue = (value) => (
  value !== null &&
  typeof value !== 'undefined' &&
  !(typeof value === 'string' && value.trim() === '')
);

export const pickFirstRenderable = (body = {}, names = []) => {
  for (const name of names) {
    if (hasRenderableValue(body[name])) {
      return body[name];
    }
  }

  return null;
};

export const findTitleValue = (body = {}, schema) => {
  const titleField = getSchemaFields(schema).find((field) => field.role === 'title');
  if (titleField && hasRenderableValue(body[titleField.name])) {
    return body[titleField.name];
  }

  return pickFirstRenderable(body, [
    'request_title',
    'part_name',
    'capability_name',
    'asset_name',
    'title',
    'name',
  ]);
};

export const getPartnerDisplayName = (partner = {}, fallback = 'Unknown Partner') => (
  partner.display_name ||
  partner.legal_name ||
  partner.partner_code ||
  partner.partner_id ||
  fallback
);

export const getMessagePartnerKey = (message = {}) => (
  message.sender_partner_id ||
  message.sender_partner?.partner_id ||
  'unknown-partner'
);

export const getCapabilitySourceId = (message = {}) => (
  message.body?.capability_source_id || message.message_uuid
);

export const getAssetSourceId = (message = {}) => (
  message.body?.asset_source_id || message.message_uuid
);

export const getInitialRequestMessage = (messages = []) => (
  messages.find((message) => isMessageKind(message, 'request', 'initial')) || messages[0] || null
);

export const getThreadTitle = (messages = [], schemaCache = {}) => {
  const initialRequest = getInitialRequestMessage(messages);
  if (!initialRequest) {
    return 'Correlation Thread';
  }

  const schema = getMessageSchema(initialRequest, schemaCache)?.schema || null;
  return findTitleValue(initialRequest.body || {}, schema) || 'Correlation Thread';
};

export const getSearchableText = (value) => {
  if (!hasRenderableValue(value)) {
    return '';
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value).toLowerCase();
    } catch {
      return String(value).toLowerCase();
    }
  }

  return String(value).toLowerCase();
};

export const buildPartnerMessageGroups = (messages = []) => {
  const groupsByPartner = new Map();

  messages.forEach((message) => {
    if (isMessageKind(message, 'request', 'initial')) {
      return;
    }

    const partnerKey = getMessagePartnerKey(message);
    if (!groupsByPartner.has(partnerKey)) {
      groupsByPartner.set(partnerKey, {
        partnerKey,
        partner: message.sender_partner || { partner_id: partnerKey },
        messages: [],
        capabilityMessages: [],
        assetMessages: [],
        otherMessages: [],
      });
    }

    const group = groupsByPartner.get(partnerKey);
    group.messages.push(message);

    if (isMessageKind(message, 'response', 'capabilities_offer')) {
      group.capabilityMessages.push(message);
      return;
    }

    if (isMessageKind(message, 'response', 'assets_offer')) {
      group.assetMessages.push(message);
      return;
    }

    group.otherMessages.push(message);
  });

  return Array.from(groupsByPartner.values()).map((group) => {
    const capabilityIds = new Set(group.capabilityMessages.map((message) => String(getCapabilitySourceId(message))));
    const capabilities = group.capabilityMessages.map((message) => {
      const capabilityId = String(getCapabilitySourceId(message));
      return {
        id: capabilityId,
        message,
        assets: group.assetMessages.filter((assetMessage) => (
          String(assetMessage.body?.capability_source_id || '') === capabilityId
        )),
      };
    });

    const standaloneAssets = group.assetMessages.filter((assetMessage) => (
      !capabilityIds.has(String(assetMessage.body?.capability_source_id || ''))
    ));

    return {
      ...group,
      capabilities,
      standaloneAssets,
    };
  });
};
